const STORAGE_KEY_PREFIX = "cmm_order_assignee_id:";

/**
 * Builds the sessionStorage key used to carry a CMM order's assignee across
 * routes when the single-order API omits `assignee_id`.
 *
 * @param orderId - CMM order identifier
 * @returns Session storage key for that order
 */
const getStorageKey = (orderId: string): string =>
  `${STORAGE_KEY_PREFIX}${orderId}`;

/**
 * Persists a non-empty assignee Firebase UID for an order in sessionStorage.
 *
 * @param orderId - CMM order identifier
 * @param assigneeId - Firebase UID of the assigned POC, or null/empty to skip
 */
export const persistCmmOrderAssigneeId = (
  orderId: string,
  assigneeId: string | null | undefined,
): void => {
  const trimmedOrderId = orderId?.trim();
  const trimmedAssigneeId = assigneeId?.trim();
  if (!trimmedOrderId || !trimmedAssigneeId) {
    return;
  }
  try {
    sessionStorage.setItem(getStorageKey(trimmedOrderId), trimmedAssigneeId);
  } catch {
    // Ignore quota / private-mode failures; navigation state remains a fallback.
  }
};

/**
 * Persists assignee UIDs for a batch of raw orders from a list API response.
 *
 * List endpoints return `assignee_id`, while the single-order endpoint returns
 * it empty, so every loaded row is cached up front to cover all the routes that
 * navigate away with only an order id (row click, View Clinical, View Outcome).
 *
 * @param orders - Raw order rows containing `identifier` and `assignee_id`
 */
export const persistCmmOrderAssigneeIds = (
  orders: Record<string, unknown>[] | null | undefined,
): void => {
  if (!orders?.length) {
    return;
  }
  orders.forEach((order) => {
    const orderId = order?.identifier;
    const assigneeId = order?.assignee_id;
    if (typeof orderId === "string" && typeof assigneeId === "string") {
      persistCmmOrderAssigneeId(orderId, assigneeId);
    }
  });
};

/**
 * Reads a previously persisted assignee Firebase UID for an order.
 *
 * @param orderId - CMM order identifier
 * @returns Stored assignee UID, or null when missing
 */
export const readCmmOrderAssigneeId = (
  orderId: string | null | undefined,
): string | null => {
  const trimmedOrderId = orderId?.trim();
  if (!trimmedOrderId) {
    return null;
  }
  try {
    const value = sessionStorage.getItem(getStorageKey(trimmedOrderId));
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  } catch {
    return null;
  }
};

/**
 * Resolves `assignee_id` from a CMM table row, preferring the top-level field
 * and falling back to the stringified raw API payload on `rowData`.
 *
 * @param rowData - Formatted CMM order table row
 * @returns Assignee Firebase UID, or null when unavailable
 */
export const resolveAssigneeIdFromCmmTableRow = (
  rowData: Record<string, unknown> | null | undefined,
): string | null => {
  if (!rowData) {
    return null;
  }

  const topLevel = rowData.assignee_id;
  if (typeof topLevel === "string" && topLevel.trim() !== "") {
    return topLevel.trim();
  }

  const serialized = rowData.rowData;
  if (typeof serialized !== "string" || serialized.trim() === "") {
    return null;
  }

  try {
    const parsed = JSON.parse(serialized) as { assignee_id?: unknown };
    const fromPayload = parsed?.assignee_id;
    if (typeof fromPayload === "string" && fromPayload.trim() !== "") {
      return fromPayload.trim();
    }
  } catch {
    return null;
  }

  return null;
};
